import { useCallback } from 'react';

import PropTypes from 'prop-types';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import { useCaptureException, ZBError } from '@zenbusiness/application-commons-logging';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import * as ROUTES from '#client/constants/routes';
import { getAppSection } from '#client/utils/getAppSection';

import * as Styled from './OrderTracker.styles';
import { VELO_CTA_GRADIENT_START } from './OrderTracker.styles';

import NoSSR from '../VeloAssistantButton/noSSR';
import VeloAvatar from '../VeloAssistantButton/VeloAvatar';

function VeloCtaRow({ topic = 'fulfillment-status' }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { businessEntityUuid } = useBusinessEntity();
  const captureException = useCaptureException();

  const clickstreamProps = {
    elementName: 'order_tracker_velo_check_status',
    elementText: 'Check status',
    pageSlug: 'order_tracker',
    dynamicProperties: {
      ai_project_name: 'velo_chat',
    },
  };

  const storeVeloNavigationContext = useCallback(() => {
    const navigationContext = {
      referrer: window.location.pathname,
      businessEntityUuid,
      section: getAppSection(window.location.pathname) || 'order_tracker',
      timestamp: Date.now(),
      clickstreamContext: {
        elementName: 'order_tracker_velo_check_status',
        elementPosition: 'order_tracker',
      },
    };

    try {
      sessionStorage.setItem('velo_source_context', JSON.stringify(navigationContext));
    } catch (error) {
      captureException(
        new ZBError(error, {
          message: 'Failed to store Velo navigation context',
          contextData: { details: { operation: 'storeVeloNavigationContext' } },
        }),
      );
    }
  }, [businessEntityUuid, captureException]);

  const handleCheckStatus = useCallback(() => {
    storeVeloNavigationContext();
    window.location.href = `${ROUTES.VELO_GUIDE_COMPANY.replace('[businessEntityUuid]', businessEntityUuid)}/?topic=${encodeURIComponent(topic)}`;
  }, [storeVeloNavigationContext, businessEntityUuid, topic]);

  const promptText = (
    <Text size="sm" color="secondary">
      <FormattedMessage id="dashboard.orderTracker.velo.prompt" />
    </Text>
  );

  const veloAvatarElement = (
    <Box display="flex" alignItems="center">
      <NoSSR>
        <VeloAvatar state="default" width={24} height={24} color={VELO_CTA_GRADIENT_START} />
      </NoSSR>
    </Box>
  );

  const askVeloText = (
    <Text size="sm" weight="medium" color="primary">
      <FormattedMessage id="dashboard.orderTracker.velo.askVelo" />
    </Text>
  );

  const checkStatusButton = (
    <Button
      variant="contained"
      color="primary"
      size="small"
      clickstreamProps={clickstreamProps}
      onClick={handleCheckStatus}
      data-testid="velo-check-status-button"
    >
      <FormattedMessage id="dashboard.orderTracker.velo.checkStatus" />
    </Button>
  );

  return (
    <Styled.VeloCtaBackground data-testid="velo-cta-row">
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'flex-start' : 'center'}
        justifyContent={isMobile ? 'flex-start' : 'space-between'}
        spacing={1}
      >
        {isMobile ? (
          <>
            {promptText}
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
              <Stack direction="row" alignItems="center" spacing={1}>
                {veloAvatarElement}
                {askVeloText}
              </Stack>
              {checkStatusButton}
            </Stack>
          </>
        ) : (
          <>
            <Stack direction="row" alignItems="center" spacing={1}>
              {promptText}
              {veloAvatarElement}
              {askVeloText}
            </Stack>
            {checkStatusButton}
          </>
        )}
      </Stack>
    </Styled.VeloCtaBackground>
  );
}

VeloCtaRow.propTypes = {
  topic: PropTypes.string,
};

export default VeloCtaRow;
